// The Network figure's other tabs (DESIGN_SPEC §11.4.8): HTTP, DNS, Connections, Flows. Background rows only appear
// when the figure shows them, faint and named by their service.
//   <HttpTable rows /> method · url (+ who sent it) · status (tinted by class) · requests · sent · received · cadence · first
//   <DnsTable rows />  name (+ CNAMEs, "never contacted") · answers (all, never clipped) · lookups · every · first
//   <ConnTable rows /> process · destination (+ name, whether the host capture saw it) · proto · count · first · cadence
//   <FlowTable rows /> proto · destination (+ name) · flows · bytes · first · seen by the guest
//   <StatusCode code />
import type { ReactNode } from "react";
import { Empty } from "@/components/ui";
import { bytes, count, rel } from "@/lib/format";
import type { NoiseService } from "@/lib/derive/types";
import { cadenceText } from "../derive/fold";
import type { ConnRow, DnsRow, FlowRow, HttpRow, SigRef } from "../derive/hosts";
export { SigBadges };

const STATUS_CLASS = (s: number) =>
  s >= 500 ? "bg-high-tint text-high-text" : s >= 400 ? "bg-medium-tint text-medium-text" : s >= 300 ? "bg-low-tint text-low-text" : s >= 200 ? "bg-benign-tint text-benign-text" : "bg-info-tint text-info-text";

function StatusCode({ code }: { code: number }) {
  return (
    <span className={`inline-flex h-[18px] items-center px-1.5 font-medium tabular-nums ${STATUS_CLASS(code)}`} title={`HTTP ${code}`}>
      {code}
    </span>
  );
}

// Suricata severity (1=highest, 4=lowest) → the shared tint tokens the rest of the figure uses.
const SIG_SEV_CLASS = (sev: number) =>
  sev <= 1 ? "bg-high-tint text-high-text"
    : sev === 2 ? "bg-medium-tint text-medium-text"
    : sev === 3 ? "bg-low-tint text-low-text"
    : "bg-info-tint text-info-text";

function SigBadge({ sig }: { sig: SigRef }) {
  const detail = [
    sig.signature,
    sig.category || null,
    sig.appProto ? `app ${sig.appProto}` : null,
    `sid ${sig.signatureId}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <span
      className={`inline-flex min-h-[18px] max-w-full items-center px-1.5 py-0.5 text-[10.5px] font-medium leading-none ${SIG_SEV_CLASS(sig.severity)}`}
      title={detail}
    >
      {/* truncate signatures that would otherwise push the badge wider
          than its parent cell — long ET sigs ('ET MALWARE Common Stealer
          Behavior - Source IP ...') were overflowing + overlapping the
          next wrapped line. */}
      <span className="block max-w-[32ch] truncate">{sig.signature}</span>
    </span>
  );
}

function SigBadges({ sigs, limit = 3, onInspect, inspectLabel = "bytes" }: { sigs: readonly SigRef[]; limit?: number; onInspect?: () => void; inspectLabel?: string }) {
  if (!sigs.length && !onInspect) return null;
  const shown = sigs.slice(0, limit);
  const more = sigs.length - shown.length;
  return (
    // gap-y-1.5 (not gap-1) so wrapped lines don't visually touch — the
    // badges are 18px tall with leading-none, which left ~0-1px of air
    // between wrapped rows and read as overlap at normal zoom.
    <ul
      className="mt-1 flex max-w-full flex-wrap items-center gap-x-1 gap-y-1.5"
      aria-label={sigs.length ? `${sigs.length} Suricata ${sigs.length === 1 ? "alert" : "alerts"}` : "flow actions"}
    >
      {shown.map((s, i) => (
        <li key={`${s.signatureId}-${i}`} className="flex min-w-0">
          <SigBadge sig={s} />
        </li>
      ))}
      {more > 0 && <li className="faint self-center text-[10.5px]">+{more}</li>}
      {onInspect && (
        <li className="flex">
          <button
            type="button"
            onClick={onInspect}
            className="inline-flex min-h-[18px] items-center border border-rule-field bg-paper px-1.5 py-0.5 text-[10.5px] font-medium leading-none text-ink hover:border-ink focus:border-ink focus:outline-none"
          >
            {inspectLabel}
          </button>
        </li>
      )}
    </ul>
  );
}

const bgClass = (bg: boolean) => (bg ? "[&_td]:text-ink-mute" : undefined);
const service = (bg: boolean, s?: NoiseService) => (bg ? ` · background${s ? ` · ${s}` : ""}` : "");
const cad = (sec?: number) => (sec ? cadenceText(sec).replace(/^every /, "") : "—");
const none = (text: ReactNode) => (
  <Empty inline rule={false}>
    {text}
  </Empty>
);

export function HttpTable({ rows, onInspectBody }: { rows: readonly HttpRow[]; onInspectBody?: (row: HttpRow) => void }) {
  if (!rows.length) return none("No HTTP requests");
  return (
    <div className="tscroll relative">
      <table className="t t--lg">
        <thead>
          <tr>
            <th>method</th>
            <th>url</th>
            <th>status</th>
            <th className="r">requests</th>
            <th className="r">sent</th>
            <th className="r">received</th>
            <th>cadence</th>
            <th className="r">first</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={bgClass(r.background)}>
              <td className="py-2 align-top font-medium text-ink">{r.method}</td>
              <td className="min-w-[28ch] whitespace-normal py-2 align-top">
                <div className={`[overflow-wrap:anywhere] ${r.background ? "" : "primary"}`} translate="no">
                  {r.url}
                </div>
                <div className="faint">
                  {r.process ? `${r.process.name || "pid"} ${r.process.pid}` : "TLS proxy · no pid"}
                  {service(r.background, r.service)}
                </div>
                <SigBadges sigs={r.sigs} onInspect={onInspectBody && (r.reqBodyB64 || r.respBodyB64) ? () => onInspectBody(r) : undefined} inspectLabel="view content" />
              </td>
              <td className="py-2 align-top">
                <span className="inline-flex flex-wrap gap-1">{r.statuses.length ? r.statuses.map((s) => <StatusCode key={s} code={s} />) : "—"}</span>
              </td>
              <td className="r py-2 align-top">{count(r.count)}</td>
              <td className="r py-2 align-top">{r.sized ? bytes(r.sent) : "—"}</td>
              <td className="r py-2 align-top">{r.sized ? bytes(r.received) : "—"}</td>
              <td className="py-2 align-top">{cad(r.cadenceSec)}</td>
              <td className="r py-2 align-top">{rel(r.firstT)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DnsTable({ rows }: { rows: readonly DnsRow[] }) {
  if (!rows.length) return none("No DNS lookups");
  return (
    <div className="tscroll relative">
      <table className="t t--lg">
        <thead>
          <tr>
            <th>name</th>
            <th>answers</th>
            <th className="r">lookups</th>
            <th>every</th>
            <th className="r">first</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={bgClass(r.background)}>
              <td className="min-w-[24ch] whitespace-normal py-2 align-top">
                <div className={`[overflow-wrap:anywhere] ${r.background ? "" : "primary"}`} translate="no">
                  {r.name}
                </div>
                <div className="faint [overflow-wrap:anywhere]" translate="no">
                  {[r.qtype || "A", r.via.length ? `via ${r.via.join(", ")}` : "", r.contacted ? "" : r.answers.length ? "never contacted" : "no answer"].filter(Boolean).join(" · ")}
                  {service(r.background, r.service)}
                </div>
              </td>
              <td className="min-w-[18ch] whitespace-normal py-2 align-top [overflow-wrap:anywhere]" translate="no">
                {r.answers.length ? r.answers.join(", ") : "—"}
              </td>
              <td className="r py-2 align-top">{count(r.lookups)}</td>
              <td className="py-2 align-top">{cad(r.cadenceSec)}</td>
              <td className="r py-2 align-top">{rel(r.firstT)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ConnTable({ rows, onInspect }: { rows: readonly ConnRow[]; onInspect?: (ip: string, port?: number) => void }) {
  if (!rows.length) return none("No connections");
  return (
    <div className="tscroll relative">
      <table className="t t--lg">
        <thead>
          <tr>
            <th>process</th>
            <th>destination</th>
            <th>proto</th>
            <th className="r">count</th>
            <th className="r">first</th>
            <th>cadence</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={bgClass(r.background)}>
              <td className="py-2 align-top">
                <div className="text-ink" translate="no">
                  {r.name || (r.pid !== undefined ? `pid ${r.pid}` : "unknown")}
                </div>
                <div className="faint tabular-nums">{r.pid ?? "no pid"}</div>
              </td>
              <td className="min-w-[22ch] whitespace-normal py-2 align-top">
                <div className={`[overflow-wrap:anywhere] ${r.background ? "" : "primary"}`} translate="no">
                  {r.dst}
                </div>
                <div className="faint [overflow-wrap:anywhere]" translate="no">
                  {[r.host ?? "no DNS name", r.mirrored ? `host capture saw ${r.mirrored}/${r.count}` : "not in the host capture"].join(" · ")}
                  {service(r.background, r.service)}
                </div>
                <SigBadges sigs={r.sigs} onInspect={onInspect ? () => onInspect(r.ip, r.port) : undefined} />
              </td>
              <td className="py-2 align-top">
                {r.op} {r.proto}
              </td>
              <td className="r py-2 align-top">{count(r.count)}</td>
              <td className="r py-2 align-top">{rel(r.firstT)}</td>
              <td className="py-2 align-top">{cad(r.cadenceSec)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FlowTable({ rows, onInspect }: { rows: readonly FlowRow[]; onInspect?: (ip: string, port?: number) => void }) {
  if (!rows.length) return none("No flows");
  return (
    <div className="tscroll relative">
      <table className="t t--lg">
        <thead>
          <tr>
            <th>proto</th>
            <th>destination</th>
            <th className="r">flows</th>
            <th className="r">bytes</th>
            <th className="r">first</th>
            <th>seen by the guest</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={bgClass(r.background)}>
              <td className="py-2 align-top">{r.proto}</td>
              <td className="min-w-[22ch] whitespace-normal py-2 align-top">
                <div className={`[overflow-wrap:anywhere] ${r.background ? "" : "primary"}`} translate="no">
                  {r.ip.includes(":") ? `[${r.ip}]` : r.ip}
                  {r.dport !== undefined && `:${r.dport}`}
                </div>
                <div className="faint [overflow-wrap:anywhere]" translate="no">
                  {r.host ?? "no DNS name"}
                  {service(r.background, r.service)}
                </div>
                <SigBadges sigs={r.sigs} onInspect={onInspect ? () => onInspect(r.ip, r.dport) : undefined} />
              </td>
              <td className="r py-2 align-top">{count(r.count)}</td>
              <td className="r py-2 align-top">{r.sized ? bytes(r.bytes) : "—"}</td>
              <td className="r py-2 align-top">{rel(r.firstT)}</td>
              <td className="py-2 align-top">{r.mirrored ? `${r.mirrored}/${r.count}` : <span className="faint">not reported</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
